import React, { useMemo } from 'react';
import { useMapSectionOrder } from '../utils/clientSettings';
import { InvestigatorFocusRequest, MapViewport, useMapViewport } from './MapViewport';
import { cameraInvestigatorName } from './digitalMapGame';
import { Location, UserLanguage } from '../api';
import { DEFAULT_LOCATION_POSITIONS, LocationPositions, Map } from './Map';
import '../styles/TabbedMapDisplay.css';

export const DUNWICH_LOCATION_POSITIONS: LocationPositions = {
  'Backwoods Country': { x: 318, y: 211, width: 121, height: 48 },
  'Whateley Farm': { x: 114, y: 88 },
  'Wizard\'s Hill': { x: 112, y: 252 },
  'Cold Springs Glen': { x: 110, y: 417 },
  'Village Commons': { x: 514, y: 386, width: 121, height: 48 },
  'Sentinel Hill': { x: 514, y: 256, width: 121, height: 48 },
  'Darke\'s Carnival': { x: 291, y: 492 },
  'Bishop\'s Brook Bridge': { x: 513, y: 533 },
  'Dunwich Village': { x: 713, y: 492 },
  'Blasted Heath': { x: 710, y: 212, width: 121, height: 48 },
  'Gardners\' Place': { x: 913, y: 91 },
  'Devil\'s Hopyard': { x: 913, y: 240 },
  'Harney Jones\' Shack': { x: 889, y: 444 },
  'LostCarcosa 1': { x: 1142, y: 202 },
  'LostCarcosa 2': { x: 1142, y: 120 },
  'AnotherTime 1': { x: 1142, y: 482 },
  'AnotherTime 2': { x: 1142, y: 400 },
};

export const KINGSPORT_LOCATION_POSITIONS: LocationPositions = {
  '607 Water St.': { x: 264, y: 162 },
  'The Rope and Anchor': { x: 410, y: 70 },
  'North Point Lighthouse': { x: 570, y: 70 },
  'St. Erasmus\'s Home': { x: 80, y: 105 },
  'Congregational Hospital': { x: 80, y: 300 },
  '7th House on the Left': { x: 112, y: 525 },
  'Central Hill': { x: 280, y: 344, width: 121, height: 48 },
  'South Shore': { x: 514, y: 402, width: 121, height: 48 },
  'Harborside': { x: 514, y: 295, width: 121, height: 48 },
  'Artists\' Colony': { x: 307, y: 550 },
  'The Hall School': { x: 513, y: 550 },
  'Neil\'s Curiosity Shop': { x: 711, y: 505 },
  'Wireless Station': { x: 793, y: 110 },
  'The Causeway': { x: 714, y: 300 },
  'Strange High House in the Mist': { x: 958, y: 70 },
  'UnknownKadath 1': { x: 1160, y: 202 },
  'UnknownKadath 2': { x: 1160, y: 120 },
  'TheUnderworld 1': { x: 1160, y: 482 },
  'TheUnderworld 2': { x: 1160, y: 400 },
};

export const INNSMOUTH_LOCATION_POSITIONS: LocationPositions = {
  'First National Grocery': { x: 80, y: 470 },
  'Gilman House Hotel': { x: 248, y: 482 },
  'Marsh Refinery': { x: 420, y: 490 },
  'Esoteric Order of Dagon': { x: 510, y: 80 },
  'Innsmouth Jail': { x: 135, y: 73 },
  'Innsmouth Jail Cell': { x: 42, y: 165, width: 50, height: 50 },
  'Falcon Point': { x: 795, y: 430 },
  'Factory District': { x: 170, y: 330, width: 121, height: 48 },
  'Church Green': { x: 407, y: 265, width: 126, height: 48 },
  'Innsmouth Shore': { x: 590, y: 417, width: 125, height: 48 },
  'Joe Sargent\'s Bus Service': { x: 746, y: 237, width: 125, height: 48 },
  'Sawbone Alley': {x: 335, y: 68, width: 130, height: 60},
  'Devil Reef': { x: 960, y: 70 },
  'Y\'ha-nthlei': { x: 963, y: 327 },
};

type TabbedMapDisplayProps = Omit<React.ComponentProps<typeof Map>, 'locations'> & {
  focusRequest?: InvestigatorFocusRequest;
  locations: Location[];
  gameId?: string;
  compact?: boolean;
  presentation?: 'camera' | 'document';
  includeDunwichCards?: boolean;
  includeKingsportCards?: boolean;
  includeInnsmouthInCurrentGame?: boolean;
  authLanguage: UserLanguage;
};

export const TabbedMapDisplay: React.FC<TabbedMapDisplayProps> = ({
  gameId,
  focusRequest,
  compact = false,
  presentation = 'camera',
  includeDunwichCards = false,
  includeKingsportCards = false,
  includeInnsmouthInCurrentGame = false,
  locations,
  authLanguage,
  riftProgress,
  deepOnesRisingTrack,
  fedsRaidInnsmouthTrack,
  ...mapProps
}) => {
  // Stable location arrays let printed boards skip rendering during camera motion.
  const [arkhamLocations, dunwichLocations, kingsportLocations, innsmouthLocations] = useMemo(() => [
    locations.filter(location => location.city !== 'Dunwich' && location.city !== 'Kingsport' && location.city !== 'Innsmouth'),
    locations.filter(location => location.city === 'Dunwich'),
    locations.filter(location => location.city === 'Kingsport'),
    locations.filter(location => location.city === 'Innsmouth'),
  ], [locations]);

  const boardImage = authLanguage === 'Ru' ? "/gameboard.jpg" : "/en_gameboard.jpg";

  const gatesOpened = locations
    .filter(l => l.type !== 'Special' && !/ (1|2)$/.test(l.name))
    .reduce((sum, loc) => sum + (loc.gates?.length || 0), 0);
  const seals = locations
    .filter(l => l.type !== 'Special' && !/ (1|2)$/.test(l.name))
    .reduce((sum, loc) => sum + (loc.isSealed ? 1 : 0), 0);

  const cameraGameId = gameId ?? mapProps.gameState.gameId ?? 'classic-map';
  const board = { width: 1280, height: 1280 * 3870 / 2553 + 72
    + (includeDunwichCards ? 643 + 72 : 0) + (includeKingsportCards ? 640 + 72 : 0)
    + (includeInnsmouthInCurrentGame ? 640 + 72 : 0) };
  const name = cameraInvestigatorName({ ...mapProps, gameState: mapProps.gameState });
  const order = useMapSectionOrder();
  const boards = [
    { name: 'Arkham', locations: arkhamLocations, positions: DEFAULT_LOCATION_POSITIONS, width: 900, height: 1600, displayHeight: 1280 * 3870 / 2553 },
    ...(includeDunwichCards ? [{ name: 'Dunwich', locations: dunwichLocations, positions: DUNWICH_LOCATION_POSITIONS, width: 1280, height: 643, displayHeight: 643 }] : []),
    ...(includeKingsportCards ? [{ name: 'Kingsport', locations: kingsportLocations, positions: KINGSPORT_LOCATION_POSITIONS, width: 1280, height: 640, displayHeight: 640 }] : []),
    ...(includeInnsmouthInCurrentGame ? [{ name: 'Innsmouth', locations: innsmouthLocations, positions: INNSMOUTH_LOCATION_POSITIONS, width: 1280, height: 640, displayHeight: 640 }] : []),
  ].sort((a, b) => order.indexOf(a.name as typeof order[number]) - order.indexOf(b.name as typeof order[number]));
  let top = 0;
  let arkhamTop = 0;
  let target: { x: number; y: number; name: string } | undefined;
  let focusTarget: { x: number; y: number } | undefined;
  for (const city of boards) {
    if (city.name === 'Arkham') arkhamTop = top;
    const place = city.locations.find(location => location.investigatorNames?.some(value => value.toLowerCase() === name?.toLowerCase()));
    const position = place && city.positions[place.name];
    if (position && name) target = { name, x: 2 + position.x * 1276 / city.width, y: top + 2 + position.y * (city.displayHeight - 4) / city.height };
    const focusPlace = focusRequest && city.locations.find(location => location.investigatorNames?.some(value => value.toLowerCase() === focusRequest.investigatorName.toLowerCase()));
    const focusPosition = focusPlace && city.positions[focusPlace.name];
    if (focusPosition) focusTarget = { x: 2 + focusPosition.x * 1276 / city.width, y: top + 2 + focusPosition.y * (city.displayHeight - 4) / city.height };
    top += city.displayHeight + 72;
  }

  const content = <div className="tabbed-map-display">
    {boards.map(city => <Map key={city.name} {...mapProps} locations={city.locations}
      positions={city.positions} language={authLanguage}
      {...(city.name === 'Arkham' ? { mapBackgroundImage: boardImage, gatesOpened, seals } : {
        mapBaseWidth: 1280, mapBaseHeight: city.displayHeight, mapAspectRatio: `1280 / ${city.displayHeight}`,
        mapBackgroundImage: city.name === 'Dunwich' ? '/dunwich.jpg' : city.name === 'Kingsport' ? '/Kingsport.jpg' : '/Insmouth.jpg',
        showMapStats: false, showTerrorTrack: false, showDunwichTerrorTrack: city.name === 'Dunwich',
        ...(city.name === 'Kingsport' ? { riftProgress } : {}),
        ...(city.name === 'Innsmouth' ? { deepOnesRisingTrack, fedsRaidInnsmouthTrack } : {}),
      })} />)}
  </div>;
  return presentation === 'document' ? content : <ClassicMapCamera gameId={cameraGameId}
    arkhamTop={arkhamTop} signature={boards.map(city => city.name).join('-')} board={board} target={target} focusRequest={focusRequest} focusTarget={focusTarget} language={authLanguage} compact={compact}>{content}</ClassicMapCamera>;
};

function ClassicMapCamera({ gameId, arkhamTop, signature, ...props }: Omit<React.ComponentProps<typeof MapViewport>, 'controller' | 'variant'> & { gameId: string; arkhamTop: number; signature: string }) {
  const controller = useMapViewport(gameId, { board: props.board,
    initialBounds: { x: 0, y: arkhamTop, width: 1280, height: 1280 * 3870 / 2553 },
    storageKey: `arkhamClassicMapCamera:${gameId}:${signature}` });
  return <MapViewport {...props} controller={controller} variant="classic" />;
}
